import React from 'react';
import {
  Box, Typography, RadioGroup, FormControlLabel, Radio,
  TextField, Button, Snackbar, Alert, Paper, Grid, Divider, Stack,
} from '@mui/material';
import DashboardLayout from '../../../shared/layout/DashboardLayout';
import { useParams } from 'react-router-dom';
import { useCuestionarioTesisTorresForm } from './hooks/useCuestionarioTesisTorresForm';

const FORM_COLORS = {
  primary: '#0d6aee',
  textMain: '#0f172a',
  textMuted: '#475569',
  border: '#dbe7f3',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    '& fieldset': { borderColor: '#cbd5e1' },
    '&:hover fieldset': { borderColor: FORM_COLORS.primary },
    '&.Mui-focused fieldset': { borderColor: FORM_COLORS.primary, borderWidth: 2 },
  },
};

const sectionPaperSx = {
  p: { xs: 2, md: 3 },
  mb: 3,
  borderRadius: 3,
  border: `1px solid ${FORM_COLORS.border}`,
  background: '#f8fbff',
  boxShadow: '0 4px 12px rgba(15,23,42,0.04)',
};

const radioSx = { color: FORM_COLORS.primary, '&.Mui-checked': { color: FORM_COLORS.primary } };

const ScaleFields = ({ label, state, setter, handleScaleChange }) => (
  <Box>
    <Typography variant="body2" sx={{ color: FORM_COLORS.textMuted, fontWeight: 600, mb: 1 }}>
      {label}
    </Typography>
    <Grid container spacing={2}>
      {['pre', 'intermedia', 'final'].map((field) => (
        <Grid item xs={4} key={field}>
          <TextField
            fullWidth
            label={field.charAt(0).toUpperCase() + field.slice(1)}
            type="number"
            value={state[field]}
            onChange={handleScaleChange(setter, field)}
            sx={fieldSx}
          />
        </Grid>
      ))}
    </Grid>
  </Box>
);

const CuestionarioTesisTorres = () => {
  const { idPaciente, idCuestionario } = useParams();
  const {
    dolorCronico, setDolorCronico,
    intensidadDolor, setIntensidadDolor,
    minimental, setMinimental,
    escalaDolor, setEscalaDolor,
    kinesiofobia, setKinesiofobia,
    catastrofismo, setCatastrofismo,
    gradacionDolorCronico, setGradacionDolorCronico,
    sf12, setSf12,
    openGood, setOpenGood,
    handleScaleChange,
    guardarDatos,
  } = useCuestionarioTesisTorresForm({ idPaciente, idCuestionario });

  return (
    <DashboardLayout>
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', py: 3 }}>
        <Paper
          elevation={0}
          sx={{ width: '100%', maxWidth: 800, p: { xs: 2, md: 4 }, borderRadius: 4, border: `1px solid ${FORM_COLORS.border}` }}
        >
          <Typography variant="h4" sx={{ textAlign: 'center', color: FORM_COLORS.textMain, fontWeight: 700, mb: 1 }}>
            Cuestionario Tesis Torres
          </Typography>
          <Typography sx={{ textAlign: 'center', color: FORM_COLORS.textMuted, mb: 4 }}>
            Valoración del dolor crónico, funcionalidad y calidad de vida.
          </Typography>

          {/* Valoración del dolor */}
          <Paper elevation={0} sx={sectionPaperSx}>
            <Typography variant="h6" sx={{ color: FORM_COLORS.textMain, fontWeight: 700, mb: 2 }}>
              Valoración inicial del dolor
            </Typography>
            <Typography variant="body2" sx={{ color: FORM_COLORS.textMuted, mb: 1 }}>
              ¿Ha tenido usted dolor durante más de 3 meses?
            </Typography>
            <RadioGroup value={dolorCronico} onChange={(e) => setDolorCronico(e.target.value)} row>
              <FormControlLabel value="si" control={<Radio sx={radioSx} />} label={<Typography sx={{ color: FORM_COLORS.textMain }}>Sí</Typography>} />
              <FormControlLabel value="no" control={<Radio sx={radioSx} />} label={<Typography sx={{ color: FORM_COLORS.textMain }}>No</Typography>} />
            </RadioGroup>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Intensidad del dolor (0-10)"
                  type="number"
                  inputProps={{ min: 0, max: 10 }}
                  value={intensidadDolor}
                  onChange={(e) => setIntensidadDolor(e.target.value)}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Puntuación Minimental"
                  type="number"
                  value={minimental}
                  onChange={(e) => setMinimental(e.target.value)}
                  sx={fieldSx}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Escalas pre / intermedia / final */}
          <Paper elevation={0} sx={sectionPaperSx}>
            <Typography variant="h6" sx={{ color: FORM_COLORS.textMain, fontWeight: 700, mb: 3 }}>
              Escalas de seguimiento (pre / intermedia / final)
            </Typography>
            <Stack spacing={3} divider={<Divider sx={{ borderColor: FORM_COLORS.border }} />}>
              <ScaleFields label="Escala numérica del dolor" state={escalaDolor} setter={setEscalaDolor} handleScaleChange={handleScaleChange} />
              <ScaleFields label="Kinesiofobia (TSK)" state={kinesiofobia} setter={setKinesiofobia} handleScaleChange={handleScaleChange} />
              <ScaleFields label="Catastrofismo (PCS)" state={catastrofismo} setter={setCatastrofismo} handleScaleChange={handleScaleChange} />
              <ScaleFields label="Gradación del dolor crónico (GCPS)" state={gradacionDolorCronico} setter={setGradacionDolorCronico} handleScaleChange={handleScaleChange} />
              <ScaleFields label="Calidad de vida (SF-12)" state={sf12} setter={setSf12} handleScaleChange={handleScaleChange} />
            </Stack>
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button variant="contained" color="primary" onClick={guardarDatos} sx={{ borderRadius: 2.5, px: 4 }}>
              Guardar respuestas
            </Button>
          </Box>
        </Paper>
      </Box>

      <Snackbar open={openGood} autoHideDuration={2000} onClose={() => setOpenGood(false)}>
        <Alert severity="success" onClose={() => setOpenGood(false)}>
          ¡Respuestas guardadas correctamente!
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default CuestionarioTesisTorres;
